import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getBadgeUrl } from './connectionsBadge';

interface LeaderOption {
  value: string;
  label: string;
  description?: string;
}

interface Props {
  options: LeaderOption[];
  selectedValue: string | null;
  onChange: (value: string) => void;
  loading?: boolean;
  placeholder?: string;
}

export const AvatarLeaderDropdown: React.FC<Props> = ({ options, selectedValue, onChange, loading, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current && !ref.current.contains(target) && panelRef.current && !panelRef.current.contains(target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // The panel is a position:fixed portal positioned from the trigger's rect, so page scroll
  // would leave it frozen mid-page. Close it on scroll/resize while open (but ignore scrolls
  // originating inside the panel's own scrollable option list).
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const selected = options.find((o) => o.value === selectedValue);
  const filtered = filter
    ? options.filter((o) => o.label.toLowerCase().includes(filter.toLowerCase()) || o.value.toLowerCase().includes(filter.toLowerCase()))
    : options;

  return (
    <div ref={ref} className="cp-leader-dd" style={{ position: 'relative', minWidth: 200 }}>
      <button
        type="button"
        className="cp-leader-dd-btn"
        onClick={() => {
          setOpen(!open);
          setFilter('');
        }}
        disabled={loading}
      >
        {selected ? (
          <>
            <img
              className="cp-leader-dd-avatar"
              src={getBadgeUrl(selected.value)}
              alt=""
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <span className="cp-leader-dd-name">{selected.label}</span>
          </>
        ) : (
          <span className="cp-leader-dd-placeholder">{loading ? 'Loading...' : placeholder || 'Select a leader'}</span>
        )}
        <svg
          className="cp-leader-dd-caret"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="cp-leader-dd-panel"
            style={{
              position: 'fixed',
              top: (ref.current?.getBoundingClientRect().bottom ?? 0) + 4,
              left: ref.current?.getBoundingClientRect().left ?? 0,
              width: ref.current?.getBoundingClientRect().width ?? 200,
              zIndex: 99999
            }}
          >
            <input
              className="cp-leader-dd-search"
              type="text"
              placeholder="Search..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              autoFocus
            />
            <div className="cp-leader-dd-list" role="listbox">
              {filtered.map((o) => (
                <div
                  key={o.value}
                  role="option"
                  aria-selected={o.value === selectedValue}
                  tabIndex={0}
                  className={`cp-leader-dd-opt${o.value === selectedValue ? ' sel' : ''}`}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onChange(o.value);
                      setOpen(false);
                    }
                  }}
                >
                  <img
                    className="cp-leader-dd-avatar"
                    src={getBadgeUrl(o.value)}
                    alt=""
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <span className="cp-leader-dd-opt-name">{o.label}</span>
                  {o.description && <span className="cp-leader-dd-opt-desc">{o.description}</span>}
                </div>
              ))}
              {filtered.length === 0 && <div className="cp-leader-dd-empty">No matches</div>}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
